import type { FC } from 'react';

import { cx } from '@/utils/cx';

type IconComponent = FC<{ className?: string }>;

export interface FeaturedIconProps {
  icon: IconComponent;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  theme?: 'modern' | 'light-outline' | 'dark-outline';
  color?: 'gray' | 'brand' | 'error' | 'warning' | 'success';
  className?: string;
}

const sizeStyles = {
  sm: {
    container: 'size-10',
    icon: 'size-5',
  },
  md: {
    container: 'size-12',
    icon: 'size-6',
  },
  lg: {
    container: 'size-14',
    icon: 'size-7',
  },
  xl: {
    container: 'size-16',
    icon: 'size-8',
  },
};

const themeStyles = {
  modern: {
    gray: {
      container: 'bg-utility-gray-100 border-8 border-utility-gray-50',
      icon: 'text-utility-gray-600',
    },
    brand: {
      container: 'bg-utility-brand-100 border-8 border-utility-brand-50',
      icon: 'text-utility-brand-600',
    },
    error: {
      container: 'bg-utility-error-100 border-8 border-utility-error-50',
      icon: 'text-utility-error-600',
    },
    warning: {
      container: 'bg-utility-warning-100 border-8 border-utility-warning-50',
      icon: 'text-utility-warning-600',
    },
    success: {
      container: 'bg-utility-success-100 border-8 border-utility-success-50',
      icon: 'text-utility-success-600',
    },
  },
  'light-outline': {
    gray: {
      container: 'bg-primary border-2 border-border-secondary',
      icon: 'text-fg-tertiary',
    },
    brand: {
      container: 'bg-primary border-2 border-utility-brand-200',
      icon: 'text-utility-brand-600',
    },
    error: {
      container: 'bg-primary border-2 border-utility-error-200',
      icon: 'text-utility-error-600',
    },
    warning: {
      container: 'bg-primary border-2 border-utility-warning-200',
      icon: 'text-utility-warning-600',
    },
    success: {
      container: 'bg-primary border-2 border-utility-success-200',
      icon: 'text-utility-success-600',
    },
  },
  'dark-outline': {
    gray: {
      container: 'bg-utility-gray-100 border-2 border-utility-gray-200',
      icon: 'text-utility-gray-600',
    },
    brand: {
      container: 'bg-utility-brand-100 border-2 border-utility-brand-200',
      icon: 'text-utility-brand-600',
    },
    error: {
      container: 'bg-utility-error-100 border-2 border-utility-error-200',
      icon: 'text-utility-error-600',
    },
    warning: {
      container: 'bg-utility-warning-100 border-2 border-utility-warning-200',
      icon: 'text-utility-warning-600',
    },
    success: {
      container: 'bg-utility-success-100 border-2 border-utility-success-200',
      icon: 'text-utility-success-600',
    },
  },
};

export const FeaturedIcon: FC<FeaturedIconProps> = ({
  icon: Icon,
  size = 'md',
  theme = 'modern',
  color = 'gray',
  className,
}) => {
  const styles = themeStyles[theme][color];
  const sizes = sizeStyles[size];

  return (
    <div
      className={cx(
        'flex items-center justify-center rounded-full',
        sizes.container,
        styles.container,
        className
      )}
    >
      <Icon className={cx(sizes.icon, styles.icon)} />
    </div>
  );
};
